/**
 * Mockup 04 · Etapa "Formulário" da modal de captura — uma pergunta por tela.
 * A · escolha única em grade (avança ao tocar) · B · escolha única em lista
 * (com descrição) · C · múltipla escolha · D · última pergunta ("Concluir
 * atendimento"). Réplica de `qr-base.css` (`.q-prog`, `.q-title`, `.q-sub`,
 * `.opt`, `.opt.sel`, `.rd`, `.bx`) — cores/paddings hardcoded (modal é SEMPRE
 * tema claro).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormFieldType, type FormAnswer, type FormField, type LeadCaptureSession } from '@troia-v3/types';

import { Button, ListPageLoading, ModalContent, ModalFooter } from '@/shared/components/ui';
// Parte 1 Task 11 — renderer + helpers compartilhados (não redefinir localmente).
import { FillField, LAYOUT_TYPES, asStringArray, isEmptyAnswer as isEmpty, type AnswerValue } from '@/shared/components/forms';
import { Icon } from '@/shared/icons';
import { useFormFields } from '@/shared/hooks/useFormFields';

export const CAPTURE_FORM_STEP_TITLE = 'Formulário de captura';

export interface CaptureFormStepProps {
  session: LeadCaptureSession;
  /** A modal chama `captureApi.submitForm(uuid, answers)` e avança para "Concluído". */
  onSubmit: (answers: FormAnswer[]) => Promise<void>;
  /** "Pular formulário" — a modal chama `captureApi.skipForm(uuid)`. */
  onSkip: () => void;
}

const SINGLE_CHOICE: ReadonlySet<FormFieldType> = new Set([FormFieldType.RADIO, FormFieldType.SELECT]);
const MULTI_CHOICE: ReadonlySet<FormFieldType> = new Set([FormFieldType.CHECKBOX, FormFieldType.MULTI_SELECT]);
/**
 * Grade (mockup A) quando os rótulos são curtos; lista (mockup B/D) quando são
 * frases. Divergência #8 (registrada no brief da Task 12): o mockup C usa
 * grade com um rótulo de 15 chars ("Fones Bluetooth") — a régua de 14 é a
 * heurística escolhida para dados reais, não uma extração literal do HTML
 * estático.
 */
const GRID_MAX_LABEL = 14;
/** Tempo para o usuário VER a opção marcada antes do auto-avanço (mockup A: "ao tocar, já avança"). */
const AUTO_ADVANCE_MS = 180;

/**
 * Mensagem do backend (`{ success:false, message }`) de um erro do axios —
 * `undefined` quando o erro não tem corpo (rede, abort, erro de JS). Sem
 * `any`: o erro chega como `unknown` no `catch`.
 */
const apiMessage = (error: unknown): string | undefined => {
  const message = (error as { response?: { data?: { message?: unknown } } })?.response?.data?.message;
  return typeof message === 'string' && message.trim() !== '' ? message : undefined;
};

/** Estilo dos botões de opção (`.opt` do mockup) — tema claro fixo (dentro do Modal). */
const optionStyle = (selected: boolean): React.CSSProperties => ({
  display: 'flex', alignItems: 'center', gap: 10, width: '100%', minHeight: 44,
  padding: '11px 13px', borderRadius: 10, textAlign: 'left', fontSize: 14, fontWeight: 500,
  border: `1px solid ${selected ? '#8b5cf6' : '#e2e6ec'}`,
  backgroundColor: selected ? '#f5f3ff' : '#ffffff', color: selected ? '#4c1d95' : '#1e293b',
  transition: 'border-color .12s, background-color .12s',
});

/** `.opt:hover` só se aplica quando NÃO selecionado — `.opt.sel` vence no mockup (mesma ordem de cascata aqui). */
const applyOptionHover = (el: HTMLButtonElement, selected: boolean, hovering: boolean): void => {
  if (selected) return;
  el.style.borderColor = hovering ? '#c4b5fd' : '#e2e6ec';
  el.style.backgroundColor = hovering ? '#faf8ff' : '#ffffff';
};

/** `.rd` / `.bx` do mockup — marcador de rádio (anel grosso) ou checkbox (quadrado preenchido). */
const Marker: React.FC<{ kind: 'radio' | 'check'; selected: boolean }> = ({ kind, selected }) =>
  kind === 'radio' ? (
    <span
      className="flex-none inline-block"
      style={{
        width: 18, height: 18, borderRadius: 9999, boxSizing: 'border-box',
        border: selected ? '5px solid #8b5cf6' : '1.5px solid #cbd5e1', backgroundColor: '#ffffff',
      }}
    />
  ) : (
    <span
      className="flex-none inline-flex items-center justify-center"
      style={{
        width: 18, height: 18, borderRadius: 5, boxSizing: 'border-box',
        border: `1.5px solid ${selected ? '#8b5cf6' : '#cbd5e1'}`,
        backgroundColor: selected ? '#8b5cf6' : '#ffffff',
      }}
    >
      {selected && <Icon name="check" size={14} style={{ color: '#ffffff' }} />}
    </span>
  );

/**
 * Etapa "Formulário" da modal de captura — uma pergunta por tela. Escolha
 * única avança sozinha ao tocar; múltipla e os demais tipos (texto, número,
 * data… via `FillField`) usam "Próxima". A última pergunta fecha com
 * "Concluir atendimento" (mockup D). Só `fieldId`/`value` importam para o
 * backend (reconciliação contra o snapshot).
 */
export const CaptureFormStep: React.FC<CaptureFormStepProps> = ({ session, onSubmit, onSkip }) => {
  const formId = session.capture.formId;
  const { data: form, isLoading, isError } = useFormFields(formId, { all: true, enabled: !!formId });

  const questions = useMemo<FormField[]>(
    () => [...(form?.fields ?? [])].filter((field) => !LAYOUT_TYPES.has(field.type)).sort((a, b) => a.order - b.order),
    [form],
  );

  const [index, setIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, AnswerValue>>({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const advanceTimer = useRef<number | null>(null);

  /** Cancela o auto-avanço do `pickSingle` — chamado ao clicar "Voltar"/"Próxima" e no unmount,
   * senão o timer de 180ms pode disparar depois do usuário já ter navegado manualmente. */
  const clearAdvanceTimer = useCallback((): void => {
    if (advanceTimer.current !== null) {
      window.clearTimeout(advanceTimer.current);
      advanceTimer.current = null;
    }
  }, []);

  useEffect(() => () => clearAdvanceTimer(), [clearAdvanceTimer]);

  const current = questions[index];
  const total = questions.length;
  const isLast = index === total - 1;
  const value = current ? answers[current.id] : undefined;
  const canAdvance = !!current && (!current.required || !isEmpty(value));

  const setAnswer = useCallback((fieldId: string, next: AnswerValue) => {
    setAnswers((prev) => ({ ...prev, [fieldId]: next }));
    setError(null);
  }, []);

  const buildAnswers = (): FormAnswer[] =>
    questions
      .filter((field) => !isEmpty(answers[field.id]))
      .map((field) => ({ fieldId: field.id, fieldType: field.type, fieldLabel: field.label, value: answers[field.id] ?? null }));

  const finish = async (): Promise<void> => {
    const missing = questions.find((field) => field.required && isEmpty(answers[field.id]));
    if (missing) {
      setIndex(questions.indexOf(missing));
      setError(`Responda "${missing.label}" antes de concluir.`);
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit(buildAnswers());
    } catch (err) {
      // A API diz QUAL campo obrigatório falta (422 do `validateRequiredAnswers`)
      // ou por que a sessão não aceita o formulário (409). Mostrar essa mensagem
      // em vez do texto genérico — `captureApi.submitForm` roda com
      // `_handleErrorLocally`, então não há toast duplicado do interceptor.
      setError(apiMessage(err) ?? 'Não foi possível enviar o formulário. Tente de novo.');
    } finally {
      setSubmitting(false);
    }
  };

  const next = (): void => {
    clearAdvanceTimer();
    if (!canAdvance) return;
    if (isLast) { void finish(); return; }
    setIndex((i) => Math.min(i + 1, total - 1));
  };

  const pickSingle = (field: FormField, optionValue: string): void => {
    setAnswer(field.id, optionValue);
    if (index < total - 1) {
      clearAdvanceTimer();
      advanceTimer.current = window.setTimeout(() => {
        advanceTimer.current = null;
        setIndex((i) => Math.min(i + 1, total - 1));
      }, AUTO_ADVANCE_MS);
    }
  };

  const toggleMulti = (field: FormField, optionValue: string): void => {
    const selected = asStringArray(value);
    setAnswer(field.id, selected.includes(optionValue) ? selected.filter((v) => v !== optionValue) : [...selected, optionValue]);
  };

  if (!formId) {
    return (
      <ModalContent>
        <p className="text-sm" style={{ color: '#64748b' }} data-testid="capture-form-missing">
          Este funil não tem formulário de captura configurado. Peça ao gestor para escolher um em Funil › Captura.
        </p>
        <ModalFooter><Button variant="primary" onClick={onSkip} data-testid="capture-form-skip">Continuar sem formulário</Button></ModalFooter>
      </ModalContent>
    );
  }
  if (isLoading) return <ModalContent><ListPageLoading message="Carregando perguntas..." /></ModalContent>;
  if (isError || !current) {
    return (
      <ModalContent>
        <p className="text-sm" style={{ color: '#64748b' }} data-testid="capture-form-empty">
          {isError ? 'Não foi possível carregar o formulário.' : 'Este modelo não tem perguntas.'}
        </p>
        <ModalFooter><Button variant="primary" onClick={onSkip} data-testid="capture-form-skip">Pular formulário</Button></ModalFooter>
      </ModalContent>
    );
  }

  const single = SINGLE_CHOICE.has(current.type);
  const multi = MULTI_CHOICE.has(current.type);
  const options = current.options ?? [];
  const useGrid = options.length >= 4 && options.every((o) => o.label.length <= GRID_MAX_LABEL);

  return (
    <div data-testid="capture-form-step">
      <ModalContent>
        {/* `.q-prog` — "Pergunta N de M" + barra segmentada; o cabeçalho da modal já
            mostra "{cliente} · {funil}" nesta etapa (ver `LeadCaptureModal.subtitle`) —
            o mockup 04 A–D começa direto aqui, sem parágrafo extra no corpo. */}
        <div className="flex items-center mb-3.5" style={{ gap: 10 }} data-testid="capture-form-progress">
          <span className="text-xs font-semibold whitespace-nowrap flex-none" style={{ color: '#64748b' }}>
            Pergunta {index + 1} de {total}
          </span>
          <div className="flex-1 flex gap-1">
            {questions.map((q, i) => (
              <i key={q.id} className="h-1 flex-1 rounded-full" style={{ backgroundColor: i <= index ? '#8b5cf6' : '#e9e5f5' }} />
            ))}
          </div>
        </div>

        <h3 className="text-base font-bold mb-1" style={{ color: '#1e293b', letterSpacing: '-0.02em' }} data-testid="capture-form-question">
          {current.label}{current.required ? ' *' : ''}
        </h3>
        <p className="text-xs mb-3.5" style={{ color: '#64748b' }}>
          {current.description
            ?? (single ? (isLast ? 'Escolha única.' : 'Escolha única — ao tocar, já avança para a próxima.') : multi ? 'Marque todas que se aplicam.' : '')}
        </p>

        {single && (
          <div className={useGrid ? 'grid grid-cols-2 gap-2' : 'flex flex-col gap-2'}>
            {options.map((option) => {
              const selected = value === option.value;
              return (
                <button
                  key={option.id} type="button" style={optionStyle(selected)} onClick={() => pickSingle(current, option.value)}
                  onMouseEnter={(e) => applyOptionHover(e.currentTarget, selected, true)}
                  onMouseLeave={(e) => applyOptionHover(e.currentTarget, selected, false)}
                  data-testid={`capture-form-option-${current.id}-${option.value}`}
                >
                  <Marker kind="radio" selected={selected} />
                  <span className="truncate">{option.label}</span>
                </button>
              );
            })}
          </div>
        )}

        {multi && (
          <div className={useGrid ? 'grid grid-cols-2 gap-2' : 'flex flex-col gap-2'}>
            {options.map((option) => {
              const selected = asStringArray(value).includes(option.value);
              return (
                <button
                  key={option.id} type="button" style={optionStyle(selected)} onClick={() => toggleMulti(current, option.value)}
                  onMouseEnter={(e) => applyOptionHover(e.currentTarget, selected, true)}
                  onMouseLeave={(e) => applyOptionHover(e.currentTarget, selected, false)}
                  aria-pressed={selected} data-testid={`capture-form-option-${current.id}-${option.value}`}
                >
                  <Marker kind="check" selected={selected} />
                  <span className="truncate">{option.label}</span>
                </button>
              );
            })}
          </div>
        )}

        {!single && !multi && (
          <FillField
            field={{ ...current, description: undefined }}
            value={value}
            onChange={(next) => setAnswer(current.id, next)}
            onPendingChange={() => undefined}
            hideLabel
          />
        )}

        {error && <p className="text-sm mt-3" style={{ color: '#ef4444' }} data-testid="capture-form-error">{error}</p>}
      </ModalContent>

      <ModalFooter>
        <div className="flex items-center w-full gap-2">
          <Button variant="ghost" icon="arrow-left" disabled={index === 0 || submitting} onClick={() => { clearAdvanceTimer(); setIndex((i) => Math.max(0, i - 1)); }} data-testid="capture-form-back">
            Voltar
          </Button>
          <span className="flex-1" />
          {!isLast && (
            <Button variant="link" disabled={submitting} onClick={onSkip} data-testid="capture-form-skip">Pular formulário</Button>
          )}
          {isLast ? (
            <Button variant="primary" icon="check" loading={submitting} disabled={!canAdvance || submitting} onClick={() => void finish()} data-testid="capture-form-finish">
              Concluir atendimento
            </Button>
          ) : (
            <Button variant="primary" icon="arrow-right" iconPosition="right" disabled={!canAdvance} onClick={next} data-testid="capture-form-next">
              Próxima
            </Button>
          )}
        </div>
      </ModalFooter>
    </div>
  );
};

CaptureFormStep.displayName = 'CaptureFormStep';
